/**
 * 認証まわりの共通定数。
 *
 * UI ラベル / Preferences キー / タイムアウトなどをここに集約する。
 * ハードコード禁止ルール（.claude/rules/no-hardcode.md）準拠。
 */

/** Capacitor Preferences に保存するキー名。 */
export const AUTH_PREF_KEYS = {
  /** SDK が管理する現在の session token（TTL 24h、SDK が refresh）。 */
  session: 'ymg_session_token',
  /** SDK が管理する refresh token。 */
  refresh: 'ymg_refresh_token',
  /** session token の失効時刻（ms epoch）。 */
  sessionExpiresAt: 'ymg_session_expires_at',
  /** refresh token の失効時刻（ms epoch）。 */
  refreshExpiresAt: 'ymg_refresh_expires_at',
  /** YMG session ID。 */
  sessionId: 'ymg_session_id',
  /** ゲストトークン。Capacitor ネイティブの初期 bootstrap に使う。 */
  guest: 'ymg_guest_token',
  /** 最後にログインした provider（UI の表示用途）。 */
  lastProvider: 'ymg_last_auth_provider',
  /** 最後のユーザーが登録済みプロバイダで認証したか。 */
  lastStatus: 'ymg_last_auth_status',
  /** Hub から取得した自分の userId（settings の問い合わせ用に表示）。 */
  uid: 'ymg_uid',
} as const

/** 認証プロバイダ識別子。 */
export type AuthProvider = 'google' | 'apple' | 'line'

/** UI に表示するプロバイダボタンのラベル。 */
export const AUTH_PROVIDER_LABELS: Record<AuthProvider, string> = {
  google: 'Google でサインイン',
  apple: 'Apple でサインイン',
  line: 'LINE でサインイン',
}

/** プロバイダ別の背景色（簡易テーマ）。 */
export const AUTH_PROVIDER_COLORS: Record<AuthProvider, { bg: string; fg: string }> = {
  google: { bg: '#ffffff', fg: '#1f1f1f' },
  apple: { bg: '#000000', fg: '#ffffff' },
  line: { bg: '#06c755', fg: '#ffffff' },
}

/** 認証エラー時の UI 定数。 */
export const AUTH_UI = {
  /** ユーザーがキャンセルしたときの挙動表示（トースト等）。 */
  cancelMessage: 'サインインをキャンセルしました',
  /** 通信エラー / プロバイダエラー時のメッセージ。 */
  errorMessage: 'サインインに失敗しました',
  /** 未対応プロバイダへのフォールバックメッセージ。 */
  unsupportedMessage: 'このプラットフォームではこのサインイン方法を利用できません',
  /** 「オンライン対戦」誘導モーダルの reason 文。 */
  reasonOnlineMatch: 'オンライン対戦を始めるにはログインが必要です',
  /** 実装途中（T13-T15）のマッチメイキング導線プレースホルダ。 */
  onlinePrepInProgress: 'オンライン対戦は準備中です',
  /** 連携先アカウントに既存データがある場合の確認タイトル。 */
  recoverConfirmTitle: 'データの上書き確認',
  /** 既存アカウント復旧時の警告文。 */
  recoverConfirmMessage:
    'このログイン先には既存データがあります。続行すると、この端末で遊んでいたゲストデータは置き換わります。よろしいですか？',
  /** 復旧確認の実行ボタン。 */
  recoverConfirmContinue: '引き継ぐ',
  /** 復旧確認のキャンセルボタン。 */
  recoverConfirmCancel: 'キャンセル',
} as const

/** プロバイダ別のサインイン後、続行できない場合に throw するエラーメッセージ。 */
export const AUTH_ERRORS = {
  sdkNotReady: '認証基盤の初期化に失敗しました',
  firebaseNotConfigured: 'Firebase 設定が未完了です（.env.* を確認してください）',
  socialLoginMissing: 'Capacitor Social Login プラグインが利用できません',
  liffNotInitialized: 'LIFF SDK が初期化されていません',
  notImplemented: 'この認証フローはまだ実装されていません',
  tokenMissing: 'ID トークンを取得できませんでした',
} as const

/**
 * ゲスト→サインイン後に /auth/link が 409 ALREADY_LINKED を返した場合の
 * リカバリモード（recoverAccount）の動作設定。
 */
export const AUTH_RECOVERY = {
  /**
   * 409 ALREADY_LINKED で recoverable=true なら自動的に recoverAccount を呼ぶ。
   * false にすると UI 側で確認ダイアログを出して、ユーザー同意後に recoverAccount を呼ぶ。
   *
   * 上書き事故を防ぐため、確認ダイアログを必須にする。
   */
  autoRecover: false,
} as const

/**
 * アカウント連携（Apple/Google）操作系 API のエンドポイント。
 *
 * - `/auth/link`   : ゲスト→連携（accountLink.ts 内で利用）
 * - `/auth/unlink` : 連携解除。プロバイダの ID Token を本人性確認用に同送する
 * - `/auth/providers` : 現在連携済みのプロバイダ一覧を取得（GET）
 */
export const ACCOUNT_LINK_ENDPOINTS = {
  link: '/auth/link',
  unlink: '/auth/unlink',
  providers: '/auth/providers',
} as const

/** 連携解除フローの UI ラベル。退会と同じく明示同意ダイアログを噛ませる。 */
export const ACCOUNT_UNLINK_UI = {
  buttonLabelPrefix: '連携を解除',
  pendingLabel: '連携解除中…',
  confirmTitle: '連携の解除',
  confirmMessage:
    'このプロバイダとの連携を解除すると、この端末ではゲストとしてプレイし直す形になります。\n他の端末で同じプロバイダから再ログインすれば、Hub 上のデータは残ります。',
  confirmContinue: '解除する',
  confirmCancel: 'キャンセル',
  successMessage: '連携を解除しました',
  failureFallback: '連携解除に失敗しました',
} as const

/** 復旧確認（409 ALREADY_LINKED → recoverAccount）の UI 補足ラベル。 */
export const ACCOUNT_RECOVER_UI = {
  pendingLabel: '復旧中…',
  successMessage: '既存のアカウントを引き継ぎました',
  failureFallback: 'アカウントの復旧に失敗しました',
  /** 復旧成功後にアプリをリロードするまでの遅延（ms）。
   *  退会フローと共通定数を流用していたが、意味論を分けるため独立定義する。 */
  reloadDelayMs: 800,
} as const

/**
 * アカウント削除（退会）API のエンドポイント。
 *
 * YMG SDK v1.2.0 仕様:
 *   1. POST /users/me/delete-request → confirmationToken 取得
 *   2. POST /users/me/delete-confirm → 確定削除（gameId + confirmationToken を body で送る）
 *
 * Step 2 を DELETE ではなく POST にしているのは、iOS WKWebView (Capacitor) が
 * DELETE リクエストの body を落とすバグを回避するため（参考: balloon-float accountDelete.ts）。
 */
export const ACCOUNT_DELETE_ENDPOINTS = {
  request: '/users/me/delete-request',
  confirm: '/users/me/delete-confirm',
} as const

/** 退会フローの失敗コード。UI 表示用メッセージは ACCOUNT_DELETE_UI 側で対応付ける。 */
export const ACCOUNT_DELETE_ERRORS = {
  notConfigured: 'API_NOT_CONFIGURED',
  notAvailable: 'NOT_AVAILABLE_ON_PLATFORM',
  noSession: 'NO_SESSION',
  /** Preferences など端末ストレージへのアクセスに失敗した（ストレージ IO 異常） */
  storageIoError: 'STORAGE_IO_ERROR',
  noConfirmationToken: 'NO_CONFIRMATION_TOKEN',
  network: 'NETWORK_ERROR',
  /** サーバー側の削除は完了したがローカルクリアに失敗した（要再起動 or 手動削除） */
  localClearFailed: 'LOCAL_CLEAR_FAILED',
  unknown: 'UNKNOWN_ERROR',
} as const

/**
 * 退会フローの UI ラベル。
 * `confirmMessage` は警告文として「取り消せない」「ハイスコア / 連携 / 招待が消える」ことを
 * 明示する。Apple / Google ストアの審査ガイドラインで「アカウント削除導線の明確さ」が
 * 要求されているため文言を曖昧にしない。
 */
export const ACCOUNT_DELETE_UI = {
  buttonLabel: 'アカウント削除（退会）',
  pendingLabel: '退会処理中…',
  confirmTitle: '退会の確認',
  confirmMessage:
    'アカウントを削除すると、ハイスコア・ログイン情報・連携アカウント・招待関係がすべて失われます。\nこの操作は取り消せません。本当に退会しますか？',
  confirmContinue: '退会する',
  confirmCancel: 'キャンセル',
  successMessage: '退会が完了しました。再起動します。',
  reloadDelayMs: 800,
  failureFallback: '退会に失敗しました。時間をおいて再度お試しください。',
} as const
